// ExpenseDetailsPage.jsx
import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import formatDate from "../../utils/formatDate";
import EmployeeMapTracker from "../../pages/EmployeeMapTracker";

export default function ExpenseDetailsPage() {
  const { empid, monthyear } = useParams();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState("");

  const [editRowId, setEditRowId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // ✅ MAP MODAL STATE
  const [mapModal, setMapModal] = useState({
    isOpen: false,
    employeeData: null,
  });

  const months = [
    { name: "Jan" }, { name: "Feb" }, { name: "Mar" }, { name: "Apr" },
    { name: "May" }, { name: "Jun" }, { name: "Jul" }, { name: "Aug" },
    { name: "Sep" }, { name: "Oct" }, { name: "Nov" }, { name: "Dec" },
  ];

  useEffect(() => {
    const now = new Date();
    setSelectedMonthYear(`${months[now.getMonth()].name} ${now.getFullYear()}`);
  }, []);

  // ---------------------------
  // FETCH EXPENSE DATA
  // ---------------------------
  const fetchExpenseData = async () => {
    if (!empid || !selectedMonthYear) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URL}/api/employee-daily-expense-report`,
        {
          params: { month_year: selectedMonthYear, employee_id: empid },
          headers: getAuthHeaders(),
        }
      );

      const data = res.data?.records || [];
      const formatted = data.map((item, i) => ({
        id: item.s_no ?? i,
        expense_id: item.expense_id,
        employee_expense_id: item.employee_expense_id,

        date: item.date,
        leaveMeeting: item.leave_meeting,
        calls: item.calls,
        routeDetail: item.route_detail,
        routeName: item.route_name,         // ✅ for map
        type: item.type,

        distance: item.distance,
        ta: item.ta,
        daHQ: item.da_hq,
        daExSt: item.da_exstation,
        daOutSt: item.da_outstation,
        hotel: item.hotel,
        other: item.other,

        employeeRemarks: item.employee_remarks,
        managerRemarks: item.manager_remarks,

        approve: item.is_approved_by_hr ? "yes" : "no",
        color: item.highlight_color,

        // ✅ extra fields passed to map
        residenceAddress: item.residence_address,
        latestLocation: item.latest_location,
        gpsStop: item.gps_stop,
        locationCount: item.location_count,
        gpsStopIdleTime: item.gps_stop_idle_time,
        visits: item.visits,
        pob: item.pob,
      }));

      setRows(formatted);
    } catch (err) {
      console.error("Error fetching employee expense data:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (monthyear) {
      setSelectedMonthYear(monthyear);
    } else {
      const now = new Date();
      setSelectedMonthYear(`${months[now.getMonth()].name} ${now.getFullYear()}`);
    }
  }, [monthyear]);

  useEffect(() => {
    fetchExpenseData();
  }, [empid, selectedMonthYear]);

  // ---------------------------
  // TOTALS
  // ---------------------------
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.distance += +r.distance || 0;
        acc.ta += +r.ta || 0;
        acc.daHQ += +r.daHQ || 0;
        acc.daExSt += +r.daExSt || 0;
        acc.daOutSt += +r.daOutSt || 0;
        acc.hotel += +r.hotel || 0;
        acc.other += +r.other || 0;
        return acc;
      },
      { distance: 0, ta: 0, daHQ: 0, daExSt: 0, daOutSt: 0, hotel: 0, other: 0 }
    );
  }, [rows]);

  const allApproved = rows.every((r) => r.approve === "yes");

  // ---------------------------
  // MAP MODAL HANDLERS
  // ---------------------------
  const openMapModal = (row) => {
    setMapModal({
      isOpen: true,
      employeeData: {
        employeeId: empid,
        trackDate: row.date,
        employeeName: row.employeeName || "",   // add to formatted if API returns it
        residenceAddress: row.residenceAddress || "",
        specificPunchTime: null,
        context: "expense",
        routeName: row.routeName || row.routeDetail || "",
        latestLocation: row.latestLocation || null,
        gpsStop: row.gpsStop || null,
        locationCount: row.locationCount || 0,
        gpsStopIdleTime: row.gpsStopIdleTime || null,
        visits: row.visits || [],
        pob: row.pob || null,
      },
    });
  };

  const closeMapModal = () => {
    setMapModal({ isOpen: false, employeeData: null });
  };

  // ---------------------------
  // EDIT MODE HANDLERS
  // ---------------------------
  const handleEdit = (row) => {
    setEditRowId(row.id);
    setEditValues({ ...row });
  };

  const handleCancel = () => {
    setEditRowId(null);
    setEditValues({});
  };

  const handleEditChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        expense_id: editValues.employee_expense_id,
        employee_id: empid,
        route_id: editValues.route_id || null,
        ta: Number(editValues.ta) || 0,
        da_hq: Number(editValues.daHQ) || 0,
        da_exstation: Number(editValues.daExSt) || 0,
        da_outstation: Number(editValues.daOutSt) || 0,
        hotel_charges: Number(editValues.hotel) || null,
        other_expense: Number(editValues.other) || 0,
        remarks: editValues.employeeRemarks || "",
        manager_remarks: editValues.managerRemarks || "",
        is_approved: editValues.approve === "yes",
        expense_date: editValues.date,
      };

      await axios.put(
        `${BASE_URL}/api/update-daily-expense/${editValues.expense_id}`,
        payload,
        { headers: getAuthHeaders() }
      );

      setRows((prev) =>
        prev.map((r) => (r.id === editValues.id ? { ...r, ...editValues } : r))
      );
      setEditRowId(null);
      setEditValues({});
      alert("UPDATED & APPROVED SUCCESSFULLY");
    } catch (err) {
      console.error("Update error:", err);
      alert(err.response?.data?.message || "Update failed, please try again");
    }
  };

  const handleApproveAll = async () => {
    const validIds = rows.filter((r) => r.expense_id).map((r) => r.expense_id);
    try {
      await axios.post(
        `${BASE_URL}/api/daily-expense-approve`,
        { expense_ids: validIds, is_approved: true, remarks: "Bulk approved" },
        { headers: getAuthHeaders() }
      );
      setRows((prev) => prev.map((r) => ({ ...r, approve: "yes" })));
      alert("ALL APPROVED");
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="p-5 text-center">Loading...</div>;

  return (
    <>
      <div className="border rounded shadow table-wrapper">
        <table className="unlock-dcr-leave-table text-sm w-full">
          <thead className="bg-blue-500 text-white">
            <tr>
              <th>S.No</th>
              <th>Date</th>
              <th>Leave</th>
              <th>Calls</th>
              <th>Route</th>
              <th>Type</th>
              <th>Distance</th>
              <th>T.A</th>
              <th>DA HQ</th>
              <th>DA Ex</th>
              <th>DA Out</th>
              <th>Hotel</th>
              <th>Other</th>
              <th>Emp Remarks</th>
              <th>Mgr Remarks</th>
              <th>Approve</th>
              <th>Map</th>        {/* ✅ NEW COLUMN */}
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, i) => {
              const isEdit = row.id === editRowId;
              const bg =
                row.color === "orange"
                  ? "bg-orange-200"
                  : row.color === "green"
                  ? "bg-green-200"
                  : "";

              return (
                <tr key={row.id} className={bg}>
                  <td>{i + 1}</td>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.leaveMeeting}</td>
                  <td>{row.calls}</td>
                  <td>{row.routeDetail}</td>
                  <td>{row.type}</td>

                  {["distance", "ta", "daHQ", "daExSt", "daOutSt", "hotel", "other"].map(
                    (field) => (
                      <td key={field} className="text-right">
                        {field === "other" && isEdit ? (
                          <input
                            type="number"
                            className="border p-1 w-20"
                            value={editValues.other}
                            onChange={(e) => handleEditChange("other", e.target.value)}
                          />
                        ) : (
                          row[field]
                        )}
                      </td>
                    )
                  )}

                  <td>{row.employeeRemarks}</td>

                  <td>
                    {isEdit ? (
                      <textarea
                        className="border p-1 w-full"
                        value={editValues.managerRemarks}
                        onChange={(e) => handleEditChange("managerRemarks", e.target.value)}
                      />
                    ) : (
                      row.managerRemarks
                    )}
                  </td>

                  {/* APPROVE */}
                  <td>
                    {isEdit ? (
                      <div className="flex gap-4">
                        <label className="flex items-center gap-1">
                          <input type="radio" name={`approve-${row.id}`} value="yes"
                            checked={editValues.approve === "yes"}
                            onChange={() => handleEditChange("approve", "yes")} />
                          Yes
                        </label>
                        <label className="flex items-center gap-1">
                          <input type="radio" name={`approve-${row.id}`} value="no"
                            checked={editValues.approve === "no"}
                            onChange={() => handleEditChange("approve", "no")} />
                          No
                        </label>
                      </div>
                    ) : (
                      <div>
                        <label className="flex items-center gap-1">
                          <input type="radio" checked={row.approve === "yes"} disabled /> Yes
                        </label>
                        <label className="flex items-center gap-1">
                          <input type="radio" checked={row.approve === "no"} disabled /> No
                        </label>
                      </div>
                    )}
                  </td>

                  {/* ✅ MAP COLUMN */}
                  <td className="text-center">
                    <button
                      onClick={() => openMapModal(row)}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                      title={`View map for ${formatDate(row.date)}`}
                    >
                      🗺️ Map
                    </button>
                  </td>

                  {/* ACTION */}
                  <td className="text-center">
                    {!isEdit ? (
                      <button onClick={() => handleEdit(row)}
                        className="px-3 py-1 border rounded bg-gray-200">
                        EDIT
                      </button>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <button onClick={handleUpdate}
                          className="px-3 py-1 bg-green-600 text-white rounded">
                          UPDATE
                        </button>
                        <button onClick={handleCancel}
                          className="px-3 py-1 bg-red-600 text-white rounded">
                          CANCEL
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr className="bg-cyan-900 text-white font-semibold">
              <td colSpan={6} className="text-right">TOTAL</td>
              <td>{totals.distance.toFixed(2)}</td>
              <td>{totals.ta.toFixed(2)}</td>
              <td>{totals.daHQ.toFixed(2)}</td>
              <td>{totals.daExSt.toFixed(2)}</td>
              <td>{totals.daOutSt.toFixed(2)}</td>
              <td>{totals.hotel.toFixed(2)}</td>
              <td>{totals.other.toFixed(2)}</td>
              <td colSpan={6}></td>   {/* ✅ updated colspan to cover new Map col */}
            </tr>
          </tfoot>
        </table>

        <div className="flex justify-center gap-4 mt-3">
          <button
            className="px-4 py-1 bg-green-700 text-white rounded"
            onClick={handleApproveAll}
            disabled={allApproved}
          >
            {allApproved ? "ALL APPROVED" : "APPROVE ALL"}
          </button>
        </div>
      </div>

      {/* ✅ MAP MODAL */}
      {mapModal.isOpen && mapModal.employeeData && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg w-full max-w-7xl h-[95vh] flex flex-col">
            <div className="flex-1 p-1 overflow-hidden">
              <EmployeeMapTracker
                key={`${mapModal.employeeData.employeeId}-${mapModal.employeeData.trackDate}-${mapModal.employeeData.context}`}
                employeeId={mapModal.employeeData.employeeId}
                trackDate={mapModal.employeeData.trackDate}
                employeeName={mapModal.employeeData.employeeName}
                residenceAddress={mapModal.employeeData.residenceAddress}
                specificPunchTime={mapModal.employeeData.specificPunchTime}
                onClose={closeMapModal}
                context={mapModal.employeeData.context}
                isModal={true}
                routeName={mapModal.employeeData.routeName}
                latestLocation={mapModal.employeeData.latestLocation}
                gpsStop={mapModal.employeeData.gpsStop}
                locationCount={mapModal.employeeData.locationCount}
                gpsStopIdleTime={mapModal.employeeData.gpsStopIdleTime}
                visits={mapModal.employeeData.visits}
                pob={mapModal.employeeData.pob}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}